import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {BruteForce} from "../BruteForce";
import {gsap} from "gsap";
import {
    SimpleReelModel,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData
} from "../../core/reel/simplereel/SimpleReelModel";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {BruteForceReelsArea} from "../reels/BruteForceReelsArea";
import {ArrayUtils} from "../utils/ArrayUtils";
import {BruteForceGameConfig} from "../BruteForceGameConfig";
import {BruteForceSound} from "../sound/BruteForceSound";
import {BruteForceScatterSymbol} from "../reels/BruteForceScatterSymbol";
import {BruteForceSymbol, BruteForceSymbolStates} from "../reels/BruteForceSymbol";
import {BruteForceXNudgeSymbol} from "../reels/BruteForceXNudgeSymbol";


export class BruteForceNormalResultPresentation implements FlowStatePresentation {
    protected _controller: FlowStateController;
    private _tl: gsap.core.Timeline | undefined;
    private _stoppedReels: boolean[];
    private _scatterCount: number = 0;
    private _isSkipped: boolean;
    private _startAnticipation: boolean;

    constructor() {

    }

    public start(flowStateController: FlowStateController) {
        this._controller = flowStateController;
        this._isSkipped = false;
        this._startAnticipation = false;
        this._controller.setSkipCallback(() => this.skip())
        this._stoppedReels = [false, false, false, false, false];
        const isFastSpin: boolean = BruteForce.apiPlugin.settings.get(APISetting.FAST_SPIN, false);

        this._tl = gsap.timeline({
            onComplete: () => {
                this.completePresentation()
            }
        });
        this._tl.add(() => {
            this._controller.setSkipCallback(() => this.skip());
        });

        let stopTime = 0;
        for (let i: number = 0; i < BruteForce.model.parsedClientReels.length; i++) {
            const reelData = BruteForce.model.parsedClientReels[i];
            const reelStopAnimation: gsap.core.Timeline = gsap.timeline();
            let stopDelay = isFastSpin ? BruteForceReelsArea.reelStopDelayFastSpin : BruteForceReelsArea.reelStopDelay;
            const currentReel: number = JSON.parse(JSON.stringify(i));

            if (currentReel + 1 < 5 && BruteForce.model.scatterAnticipationReels[currentReel + 1]) {
                stopDelay += isFastSpin ? BruteForceGameConfig.SCATTER_ANTICIPATION_DELAY_FAST : BruteForceGameConfig.SCATTER_ANTICIPATION_DELAY_NORMAL;
            }

            reelStopAnimation.add(this.preventSkipOnLastReel(i));
            reelStopAnimation.add(BruteForce.allreelsView.reelModels[i].stop(reelData));
            reelStopAnimation.add(() => {
                BruteForce.allreelsView.reelModels[i].reelView.stickyContainer.alpha = 1;
            });

            BruteForce.allreelsView.reelModels[i].events?.once(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => this.onReelStop(data));
            BruteForce.allreelsView.reelModels[i].events?.once(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => this.onReelBounce(data));

            this._tl.add(reelStopAnimation, stopTime);
            stopTime += stopDelay;
        }

        const padding: number = BruteForce.gameFlowController.calculatePaddingDuration(this._tl.totalDuration());
        if (padding > 0) {
            this._tl.shiftChildren((BruteForce.apiPlugin.gameClientConfiguration.minimumSpinTime / 1000) - this._tl.totalDuration());
        }
    }

    private preventSkipOnLastReel(reelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (reelId == 4) {
            tl.add(() => {
                this._isSkipped = false;
                this._controller.setSkipCallback();
            }, 0); //prevents useless skips when last reel has started its stop anyway.
        }
        return tl;
    }

    private skip(): void {
        this._controller.setSkipCallback();
        this._tl?.kill();
        this._isSkipped = true;
        this._tl = gsap.timeline({
            onStart: () => {
                this._controller.setSkipCallback();
            },
            paused: true,
            onComplete: () => {
                this.completePresentation();
            }
        });

        BruteForce.allreelsView.reelModels.forEach((model: SimpleReelModel) => {
            model.stepDurationValue = 0.003;
        });
        BruteForce.anticipationView.stopCurrentReelAnticipation(3);

        let reelStopTime: number = 0;
        for (let i = 0; i < BruteForce.model.reels.length; i++) {
            const reel = BruteForce.allreelsView.reelModels[i];

            if (reel.state == SimpleReelModelState.STOPPED || reel.state == SimpleReelModelState.IDLE) {
                continue;
            }
            if (reel.state == SimpleReelModelState.STOP) {
                continue;
            }

            if (reel.state == SimpleReelModelState.SPIN) {
                let stopAnimation;
                stopAnimation = reel.stop(BruteForce.model.parsedClientReels[i]);
                this._tl.add(stopAnimation.play(0), i * 0.1 + 0.1);
            }
        }
        // console.log("skiptl total duration:", this._tl.totalDuration());
        this._tl.play(0);
        this._tl.to(this, {duration: 0.05});
    }

    private onReelBounce(data: SimpleReelModelEventData) {
        this.stoppingAnimationAndSounds(data.reel);
    }

    private onReelStop(data: SimpleReelModelEventData) {
        this.postStopBounceAnimAndSounds(data.reel);
    }

    private completePresentation() {
        if (BruteForceSound.soundPlayer.fetchSoundPlayer(BruteForceSound.SCATTER_ANTICIPATION)?.isPlaying) {
            BruteForceSound.stopSound(BruteForceSound.SCATTER_ANTICIPATION);
        }

        if (BruteForceSound.soundPlayer.fetchSoundPlayer(BruteForceSound.SCATTER_ANTICIPATION_REEL_STOP)?.isPlaying) {
            BruteForceSound.stopSound(BruteForceSound.SCATTER_ANTICIPATION_REEL_STOP);
        }

        let noOFScatter: number = 0;
        BruteForce.model.scatterLandedPos.forEach((pos: number) => {
            if (pos > -1) {
                noOFScatter++;
            }
        })
        if (noOFScatter === 2 && this._startAnticipation) {
            BruteForceSound.playTrack(BruteForceSound.SCATTER_ANTICIPATION_FAIL);
        } else if (noOFScatter >= 3 && this._startAnticipation) {
            BruteForceSound.playTrack(BruteForceSound.SCATTER_ANTICIPATION_WIN);
        }

        if (this._startAnticipation) {
            BruteForceSound.fadeOutAndStopSound({
                name: BruteForceSound.MAIN_GAME_MUSIC_CRUSHED,
                from: 1,
                to: 0,
                duration: 10
            });
            BruteForceSound.fadeMusic(0.5, 10);
            BruteForceSound.fadeMusic(1, 2000);
        }
        let delay: number = 0.5; // #128 increased to avoid controller finishing first then 5th reel stop
        gsap.delayedCall(delay, () => {

            BruteForce.model.scatterAnticipationCopy = [false, false, false, false, false];
            this._scatterCount = 0;
            BruteForce.allreelsView.reelModels.forEach((model: SimpleReelModel) => {
                model.stepDurationValue = 0.03;
            });
            const sctterTl: gsap.core.Timeline = gsap.timeline();
            BruteForce.allreelsView.reelModels.forEach((item: SimpleReelModel, reelID: number) => {
                if (BruteForce.model.scatterLandedPos[reelID] > -1 && reelID !== 4) {
                    const scatterSymbol: BruteForceScatterSymbol = (<BruteForceScatterSymbol>item.getStopSymbolAt(BruteForce.model.scatterLandedPos[reelID]));
                    sctterTl.add(scatterSymbol.setState(BruteForceSymbolStates.NORMAL), 0);
                }
            });

            BruteForce.anticipationView.stopCurrentReelAnticipation(3);
            BruteForce.anticipationView.stopCurrentReelAnticipation(4);

            this._isSkipped = false;
            this._startAnticipation = false;
            this._controller.setSkipCallback();
            this._controller.finish();
            delete this._tl;
        });


    }

    private nearWin(reelNo: number): gsap.core.Timeline {
        const nearwinTl: gsap.core.Timeline = gsap.timeline({});
        if (BruteForce.model.scatterLandedPos[reelNo] > -1) {
            const scatterSym: BruteForceScatterSymbol = <BruteForceScatterSymbol>BruteForce.allreelsView.reelModels[reelNo].getStopSymbolAt(BruteForce.model.scatterLandedPos[reelNo]);
            BruteForce.gameContainer.screenShake(0.1, 60, false, 10);
            this._scatterCount++;
            BruteForceSound.playTrack(BruteForceSound.SCATTER_LANDS[JSON.parse(JSON.stringify(this._scatterCount)) - 1]);
            if (this._scatterCount >= 2) {
                if (BruteForceSound.soundPlayer.fetchSoundPlayer(BruteForceSound.SCATTER_LANDS[JSON.parse(JSON.stringify(this._scatterCount)) - 2])?.isPlaying) {
                    BruteForceSound.stopSound(BruteForceSound.SCATTER_LANDS[JSON.parse(JSON.stringify(this._scatterCount)) - 2]);
                }
            }
            nearwinTl.add(scatterSym.stopTeaseEffect(), 0);
            nearwinTl.add(scatterSym.setState(BruteForceSymbolStates.LANDING).timeScale(2), 0);
            if (!this._isSkipped) {
                if (BruteForce.model.scatterAnticipationReels.includes(true)) {
                    if (reelNo !== 4) {
                        nearwinTl.add(() => {
                            scatterSym.playAnimationOnLoop("anticipation", true)
                        }, nearwinTl.duration());
                    }
                }
            }
        }

        if (this._scatterCount === 2 && !this._isSkipped && reelNo !== 4 && BruteForce.model.scatterAnticipationReels.includes(true)) {
            if (!this._startAnticipation) {
                BruteForceSound.playTrack(BruteForceSound.SCATTER_ANTICIPATION);
                this._startAnticipation = true;
                BruteForceSound.fadeMusic(0, 10);
                BruteForceSound.fadeInAndStartSound({
                    name: BruteForceSound.MAIN_GAME_MUSIC_CRUSHED,
                    from: 0,
                    to: 1,
                    duration: 10
                });
            } else {
                if (reelNo === 3 && BruteForce.model.scatterLandedPos[reelNo] === -1) {
                    BruteForceSound.playTrack(BruteForceSound.SCATTER_ANTICIPATION_REEL_STOP);
                }
            }
        }

        if (this._scatterCount >= 2 && reelNo !== 4 && !this._isSkipped && BruteForce.model.scatterAnticipationReels.includes(true)) {
            BruteForce.anticipationView.startAnticipationOnNextReel(JSON.parse(JSON.stringify(reelNo)) + 1);
            BruteForce.allreelsView.reelModels[JSON.parse(JSON.stringify(reelNo)) + 1].stepDurationValue = 0.3;
        }

        return nearwinTl;
    }

    private stoppingAnimationAndSounds(reelID: number): void {
        //playing mid symbol landing animation
        const currentSym: SimpleReelModelSymbolData[] = BruteForce.allreelsView.reelModels[reelID].symbols;

        currentSym.forEach((sym: SimpleReelModelSymbolData) => {
            let bruteforceSym: BruteForceSymbol = (<BruteForceSymbol>sym.positionable);
            if (bruteforceSym.symbolName.includes("XN")) {
                if (!BruteForce.allreelsView.reelModels[reelID].reelView.isReelContainingFullNudge) {
                    switch (bruteforceSym.symbolName) {
                        case "XN_1":
                            BruteForceSound.playTrack(BruteForceSound.NUDGE_LAND[0]);
                            break;
                        case "XN_2":
                            BruteForceSound.playTrack(BruteForceSound.NUDGE_LAND[1]);
                            break;
                        case "XN_3":
                            BruteForceSound.playTrack(BruteForceSound.NUDGE_LAND[2]);
                            break;
                    }
                    (<BruteForceXNudgeSymbol>bruteforceSym).playSpineAnimation("landing2_feature", false).add((<BruteForceXNudgeSymbol>bruteforceSym).playSpineAnimation("normal", false)).timeScale(2);
                }
                BruteForce.gameContainer.screenShake(0.15, 100, false);
            }
        });
    }

    private postStopBounceAnimAndSounds(currentReelNo: number): gsap.core.Timeline {
        const scatterTl: gsap.core.Timeline = gsap.timeline();
        BruteForceSound.playTrack(BruteForceSound.REELS_LANDING[currentReelNo], 1);

        if (BruteForceGameConfig.isScatterAnticipationOnReelActive[currentReelNo]) {
            BruteForce.anticipationView.stopCurrentReelAnticipation(currentReelNo);

        }

        //to stop tease effect for scatters that are buffer symbols
        const scatterBufferTl: gsap.core.Timeline = gsap.timeline();
        BruteForce.allreelsView.reelModels.forEach((item: SimpleReelModel) => {
            const symArray = item.symbols;
            symArray.forEach((sym: SimpleReelModelSymbolData) => {
                if ((<BruteForceSymbol>sym.positionable).symbolName === "SC") {
                    let scatterSymbol: BruteForceScatterSymbol = <BruteForceScatterSymbol>sym.positionable;
                    if (sym.reelPosition < 0 || sym.reelPosition > 4) {
                        scatterBufferTl.add(scatterSymbol.stopTeaseEffect(), 0);
                    }
                }
            });
        });

        scatterTl.add(scatterBufferTl, 0);
        scatterTl.add(this.nearWin(currentReelNo), 0);


        this._stoppedReels[currentReelNo] = true;

        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            BruteForceSound.stopSound(BruteForceSound.REELS_SPINNING);
        }

        // console.log("onReelstop currentReel:", currentReelNo,);
        scatterTl.play(0);
        return scatterTl;
    }

}